import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';

import { API } from '@/lib/api';
import { httpRequest, httpErrorUserMessage } from '@/lib/http';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const err = p.get('error');
    if (err === 'google_oauth') setError('Connexion Google annulee.');
    else if (err === 'google_invalid') setError('Session Google invalide. Reessayez.');
    if (err) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await httpRequest(`${API}/auth/login`, {
        method: 'POST',
        body: { email, password },
      });
      setUser(data);
      toast.success('Connexion reussie');
      if (localStorage.getItem('pending_analysis_token')) {
        sessionStorage.setItem('leaseoptimizer_open_payment_after_claim', '1');
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(httpErrorUserMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await httpRequest(`${API}/auth/google/start`, { method: 'POST', body: {} });
      if (data?.url) window.location.href = data.url;
      else setError('Reponse Google invalide.');
    } catch (err) {
      setError(httpErrorUserMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1c1c1e] flex" data-testid="login-page">
      {/* Left side - form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <div className="w-8 h-8 bg-green-600 flex items-center justify-center rounded">
              <span className="text-[#f5f5f7] font-black text-sm font-heading">LM</span>
            </div>
            <span className="font-bold text-lg tracking-tight font-heading text-[#f5f5f7]">LeasingMalin.com</span>
          </Link>

          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold mb-2 font-heading text-[#f5f5f7]">Connexion</h1>
          <p className="text-base text-[#f5f5f7]/50 mb-8">Accedez a votre espace d'analyse</p>

          {error && (
            <div className="bg-red-950/40 border border-red-500/30 text-red-400 text-sm px-4 py-3 mb-6 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#f5f5f7]/80 mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#f5f5f7]/40" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="pl-10 h-11 bg-[#2c2c2e] border-white/8 text-[#f5f5f7] placeholder:text-[#f5f5f7]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0071e3] rounded-lg"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-[#f5f5f7]/80 mb-1.5 block">Mot de passe</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#f5f5f7]/40" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="pl-10 pr-10 h-11 bg-[#2c2c2e] border-white/8 text-[#f5f5f7] placeholder:text-[#f5f5f7]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0071e3] rounded-lg"
                  required
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f5f5f7]/40 hover:text-[#f5f5f7]/60">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 btn-primary rounded-lg text-sm"
              data-testid="login-submit-btn"
            >
              {loading ? <div className="spinner mx-auto" style={{ width: 18, height: 18, borderWidth: 2 }} /> : 'Se connecter'}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/8" /></div>
            <div className="relative flex justify-center text-xs uppercase tracking-widest text-[#f5f5f7]/40 bg-[#1c1c1e] px-4">ou</div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            data-testid="google-login-btn"
            className="w-full h-11 flex items-center justify-center gap-3 border border-white/8 text-sm font-medium text-[#f5f5f7]/80 hover:bg-[#2c2c2e] transition-[opacity,transform] duration-[180ms] rounded-lg disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continuer avec Google
          </button>

          <p className="text-center text-sm text-[#f5f5f7]/50 mt-8">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-green-400 font-semibold hover:text-green-300" data-testid="login-register-link">
              Creer un compte
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - visual */}
      <div className="hidden lg:flex w-1/2 bg-black items-center justify-center p-12">
        <div className="max-w-md text-center">
          <h2 className="text-3xl font-bold text-[#f5f5f7] mb-4 tracking-tight font-heading">
            Economisez sur vos mensualites
          </h2>
          <p className="text-[#f5f5f7]/40 text-base leading-relaxed">
            Des centaines d'automobilistes ont deja reduit leurs mensualites apres verification de leur contrat.
          </p>
          <div className="mt-10 p-8 bg-white/5 border border-white/[0.12] rounded-xl">
            <p className="text-5xl font-black text-green-400 mb-2 font-heading">-18%</p>
            <p className="text-sm text-[#f5f5f7]/40">Reduction moyenne constatee</p>
          </div>
        </div>
      </div>
    </div>
  );
}
